import React, { Suspense, lazy, useState, useEffect, useRef, memo } from 'react';

import {
  IconSpinner,
  LayoutComposer,
  LandingSearch,
  Newsletter,
} from '../../components/index.js';
import SloganSection from './HomePage/Slogan.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import BannerAnimated from '../LandingPage/Banner/BannerAnimated.js';
import SectionWhyList from './SectionWhyList/SectionWhyList.js';
import ModelLinksSection from './ModelLinksSection/ModelLinksSection.js';
import HomepageFAQ from './HomepageFAQ/HomepageFAQ.js';

// Import above-the-fold components directly (no lazy loading needed)
import PremiumListings from '../../components/PremiumListings/PremiumListings.js';
import FeaturedListing from '../FooterContainer/FeaturedListing/FeaturedListing.js';

// Lazy load below-the-fold components
const PriceReductions = lazy(() => import('../../components/PriceReductions/PriceReductions.js'));
const RecentListings = lazy(() => import('../../components/RecentListings/RecentListings.js'));
const FeaturedListings = lazy(() => import('../../components/FeaturedListings/FeaturedListings.js'));

// Check if large viewport (desktop)
const isLargeViewport = () => typeof window !== 'undefined' && window.innerWidth > 768;

import { validProps } from './Field';

import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';

const getMetadata = (meta, schemaType, fieldOptions, marketplaceRootURL) => {
  const { pageTitle, pageDescription, socialSharing, keywords: pageKeywords } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // keywords for SEO meta tag
  const keywords = validProps(pageKeywords, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data

  // Enhanced WebPage schema with proper linking to WebSite
  const currentUrl = marketplaceRootURL || '';
  const pageSchemaForSEO = {
    '@context': 'https://schema.org',
    '@type': schemaType || 'WebPage',
    '@id': currentUrl ? `${currentUrl}#webpage` : undefined,
    url: currentUrl || undefined,
    name: title,
    description: description,
    isPartOf: currentUrl ? {
      '@id': `${currentUrl}#website`,
    } : undefined,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  // Remove undefined values
  Object.keys(pageSchemaForSEO).forEach(key =>
    pageSchemaForSEO[key] === undefined && delete pageSchemaForSEO[key]
  );

  return {
    title,
    description,
    keywords,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

// Get responsive rootMargin based on viewport
// Mobile screens are smaller, so we need smaller preload distances
const getResponsiveRootMargin = () => {
  if (typeof window === 'undefined') return '300px';
  const width = window.innerWidth;
  if (width <= 480) return '150px';  // Mobile: smaller preload
  if (width <= 768) return '250px';  // Tablet: medium preload
  return '500px';                     // Desktop: larger preload
};

// Get responsive minHeight based on viewport
const getResponsiveMinHeight = (desktopHeight) => {
  if (typeof window === 'undefined') return desktopHeight;
  const width = window.innerWidth;
  if (width <= 480) return Math.round(desktopHeight * 0.6);  // Mobile: 60% height
  if (width <= 768) return Math.round(desktopHeight * 0.8);  // Tablet: 80% height
  return desktopHeight;
};

// Optimized Lazy Load Wrapper - responsive for mobile/tablet/desktop
const LazyLoadSection = memo(({ children, minHeight = 200 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [responsiveHeight, setResponsiveHeight] = useState(minHeight);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    if (!currentRef) return;

    // Set responsive height on mount
    setResponsiveHeight(getResponsiveMinHeight(minHeight));

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0,
        rootMargin: getResponsiveRootMargin(),
      }
    );

    observer.observe(currentRef);

    return () => observer.disconnect();
  }, [minHeight]);

  // Use a placeholder div with responsive minHeight
  if (!isVisible) {
    return <div ref={sectionRef} style={{ minHeight: responsiveHeight }} />;
  }

  return <div ref={sectionRef}>{children}</div>;
});

//////////////////
// Page Builder //
//////////////////

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @param {Object} props.pageAssetsData
 * @param {Array<Object>} props.pageAssetsData.sections
 * @param {Object} props.pageAssetsData.meta
 * @param {Object} props.pageAssetsData.meta.pageTitle
 * @param {Object} props.pageAssetsData.meta.pageDescription
 * @param {Object} props.pageAssetsData.meta.socialSharing
 * @param {boolean?} props.inProgress
 * @param {Object?} props.error
 * @param {ReactNode?} props.fallbackPage
 * @param {string} props.schemaType type from schema.org (e.g. 'Article', 'Website')
 * @param {string?} props.currentPage name of the current page based on route configuration
 * @param {Object} props.options
 * @param {Object<string,FieldComponentConfig>} props.options.fieldComponents custom field components
 * @returns {JSX.Element} page component
 */
const LandingPageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    ...pageProps
  } = props;

  // Get configuration for marketplaceRootURL
  const config = { marketplaceRootURL: process.env.REACT_APP_MARKETPLACE_ROOT_URL };

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  const pageMetaProps = {
    ...getMetadata(meta, schemaType, options?.fieldComponents, config.marketplaceRootURL),
    title: 'Rennzone — The Global Porsche Marketplace | Cars, Parts & Memorabilia',
    description:
      'Buy and sell Porsche cars, parts, books and memorabilia. Rennzone is the only Porsche-exclusive global marketplace. 993, 997, GT3, and more.',
  };

  const layoutAreas = `
    topbar
    main
    footer
  `;
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} />
              </Topbar>
              <Main as="main" className={css.main}>
                {/* Above the fold - Load immediately for best LCP */}
                <BannerAnimated />
                <PremiumListings />

                {/* FeaturedListing: Immediate on desktop, lazy on mobile/tablet */}
                {isLargeViewport() ? (
                  <FeaturedListing />
                ) : (
                  <LazyLoadSection minHeight={300}>
                    <FeaturedListing />
                  </LazyLoadSection>
                )}

                {/* Below the fold - Single Suspense boundary for lazy components */}
                <Suspense fallback={<LoadingSpinner />}>
                  {/* Featured Listings */}
                  <LazyLoadSection minHeight={400}>
                    <FeaturedListings />
                  </LazyLoadSection>

                  {/* Recent Listings */}
                  <LazyLoadSection minHeight={400}>
                    <RecentListings />
                  </LazyLoadSection>

                  {/* Price Reductions */}
                  <LazyLoadSection minHeight={400}>
                    <PriceReductions />
                  </LazyLoadSection>
                </Suspense>

                {/* Non-lazy loaded sections - use intersection observer only */}
                {/* {sections.length > 0 && (
                  <LazyLoadSection minHeight={200}>
                    <SectionBuilder sections={sections} options={options} />
                  </LazyLoadSection>
                )} */}
                <LazyLoadSection minHeight={220}>
                  <ModelLinksSection />
                </LazyLoadSection>

                <LazyLoadSection minHeight={400}>
                  <SectionWhyList />
                </LazyLoadSection>

                <LazyLoadSection minHeight={150}>
                  <SloganSection />
                </LazyLoadSection>

                <LazyLoadSection minHeight={200}>
                  <LandingSearch />
                </LazyLoadSection>

                {/* HomepageFAQ is rendered eagerly (no LazyLoadSection) so the
                    brand-disambiguation Q&A is in the server-rendered HTML and
                    crawlable by Google. The /vs/rennlist alternative page and
                    homepage FAQ together capture renn* brand-confusion searches. */}
                <HomepageFAQ />

                <LazyLoadSection minHeight={250}>
                  <Newsletter />
                </LazyLoadSection>
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default LandingPageBuilder;
